/**
 * MultiplicityBadge (Sprint 4.3.2, PROD-651).
 *
 * Small pill that surfaces the runtime-multiplicity count for an Action
 * node. Rendered next to the Action label IN RUNTIME MODE when N > 1
 * activities instantiate the same action def. Hidden in Design Mode.
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/docs/
 *   getting-started/orchestration-ontology.md
 *   ("...depending on how your care flow is structured, a track or step
 *    (and thus the actions in it) might be activated more than once.")
 *
 * Example: an action labelled "Form" with 3 mounted FHIR Tasks renders
 * as `Form: 3 activities`.
 *
 * HC2 token-only. HC5 zero em / en dashes. HC6 a11y: `role="status"`
 * + `aria-label="Multiplicity, 3 activities"`.
 */

export interface MultiplicityBadgeProps {
  /** Action label to prefix on the badge (e.g. "Form", "Email"). */
  actionLabel: string;
  /** Number of runtime activities instantiating the action. */
  count: number;
  /** Anchor id to disambiguate test-ids when many badges co-exist. */
  anchorId?: string;
  className?: string;
}

export function MultiplicityBadge({
  actionLabel,
  count,
  anchorId,
  className,
}: MultiplicityBadgeProps) {
  const testId = `multiplicity-badge-${anchorId ?? 'default'}`;
  const text = `${actionLabel}: ${count} activities`;
  const ariaLabel = `Multiplicity, ${count} activities for ${actionLabel}`;
  return (
    <span
      role="status"
      aria-label={ariaLabel}
      data-testid={testId}
      data-count={count}
      title={text}
      className={[
        'inline-flex w-fit items-center rounded-signal-pill px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wider ring-1 ring-inset',
        'bg-signal-primary-100 text-signal-primary-700 ring-signal-primary-300',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {text}
    </span>
  );
}
